/**
 * GameSession — the scenes' single source of truth.
 *
 * Wires the (already-built, already-tested) lower layers into one object the Phaser
 * scenes drive: it holds the live world (a working copy of the Save), the loaded
 * GameDataset, a BattleDeps for the engine, and the voice / chat-director /
 * story-director pipeline. It owns *persistence + orchestration*, never game rules —
 * those stay in core/ and society/.
 *
 * Hard guarantees (CLAUDE.md):
 *  - Never throws on missing data: the dataset falls back to fixtures, and every IO
 *    boundary (IndexedDB, fetch, the proxy) is guarded so a tired player never hits a
 *    crash on open.
 *  - All randomness flows through core/rng (deterministic from the world seed).
 *  - No secrets: the LLM key never touches the client; the proxy is reached via the
 *    Voice layer with a user-entered bearer token only.
 *  - Returning after days away is a soft-capped, warm welcome-back — never a backlog.
 */
import type {
  Save,
  Settings,
  PlayerAvatar,
  PokemonInstance,
  SpeciesData,
  MoveData,
  MoveSlot,
  SimTrainer,
  Beat,
  Emotion,
  LoreBeat,
  PlayerContext,
  SpeakerContext,
  Voice,
  VoiceLine,
  ChatChannel,
  DexState,
  GeneratedEncounter,
  QuestState,
  CareState,
  LeagueState,
  RivalState,
  OverworldPos,
  QuestDef,
  QuestEvent,
  QuestReward,
} from '@/shared';
import { emptyQuestState, emptyCareState, emptyLeagueState } from '@/shared';
import {
  loadGame,
  newSave,
  DEFAULT_SETTINGS,
  DEFAULT_AVATAR,
  exportSave as exportSaveString,
  importSave as importSaveString,
  getServerSave,
  putServerSave,
  listServerSaves,
  deleteServerSave,
  listSavesInDb,
  loadSlotFromDb,
  saveSlotToDb,
  deleteSlotFromDb,
  DEFAULT_SLOT,
  fillNaturalMoveset,
} from '@/store';
import { loadDataset } from '@/data';
import type { GameDataset } from '@/data';
import { buildDataIndex } from '@/data';
import {
  makeSociety,
  makeInstance,
  makeOvernightTick,
  liveBeats,
  showOffBeat,
  castBeat,
  playerTeamReactionBeats,
} from '@/society';
import { TemplateVoice, LLMVoice } from '@/voice';
import { ChatDirector, playerMessageBeat, questBeat, type QuestPhase } from '@/chat-director';
import { TemplateStoryVoice, codexEntryById, mewtwoThreadBeats, buildStoryRequest } from '@/lore';
import {
  makeRng,
  hashString,
  computeStats,
  NATURES,
  expForLevel,
  checkEvolution,
  evolveInstance,
  friendshipAfter,
  FRIENDSHIP_ON_WIN,
  FRIENDSHIP_ON_GYM_WIN,
  FRIENDSHIP_ON_LEVEL_UP,
  FULLNESS_FLOOR,
  type CareAction,
  type Rng,
  type BattleDeps,
} from '@/core';
import { applyRareCandy } from '@/core/items.js';
import {
  advanceQuests,
  acceptQuest as acceptQuestRule,
  turnInQuest as turnInQuestRule,
  offerableQuests as offerableQuestsRule,
  type QuestWorldSlice,
} from '@/core/quests.js';
import { ALL_QUESTS } from '@/data/quests/index.js';
import { leagueMemberByOrder, type LeagueMember } from '@/data/quests/league.js';
import {
  LEAGUE_BOUT_COUNT,
  advanceOnWin as advanceLeagueOnWin,
  recordClear as recordLeagueClearRule,
  startRun as startLeagueRunRule,
} from '@/core/league.js';
import {
  speciesTypeLookup,
  buildQuestWorldSlice,
  type QuestBeatFactory,
  type GrantRewardOpts,
  type GrantRewardResult,
} from './quests.js';
import { teamSnapshot, championCrownedBeat } from './league.js';
import {
  careActOn,
  ensureCareRecord,
  upsertCareRecord,
  easeCareForAbsence,
  getCareRecord,
  wearCosmetic,
} from './care.js';
import {
  chooseRival,
  buildRivalParty,
  sparLeadLevel,
  recordSpar,
  rivalCheerBeat,
  rivalMilestoneBeat,
} from './rival.js';
import { rivalBadgesForPlayer, pickRivalLine, fillRivalLine, type RivalLinePhase } from '@/core';
import { RIVAL_ROLE } from '@/data/quests/index.js';

import { makeStarter } from './starter.js';
import { composeWelcomeBack } from './welcomeBack.js';
import { addInGameDays, wallClockDaysElapsed } from './date.js';
import type {
  LoadOrNewResult,
  CreateNewOptions,
  WelcomeBack,
  ChatExchange,
  CreditEntry,
  BattleOutcome,
  SaveSummary,
} from './types.js';

/** Soft cap on overnight replay (mirrors society MAX_TICK_DAYS — a lovely return). */
const SOFT_CAP_DAYS = 5;

/** Default society size — a busy, populated town (hundreds-capable; injectable). */
const DEFAULT_SOCIETY_SIZE = 120;

/** A minimal dataset bundle (real generated set, or the hermetic fixtures fallback). */
interface DatasetBundle {
  readonly dataset: GameDataset | null;
  readonly species: readonly SpeciesData[];
  readonly moves: readonly MoveData[];
  readonly index: ReturnType<typeof buildDataIndex>;
}

/**
 * The server-synced-save client the session uses for the durable source of truth.
 * Injectable so tests can stub it (and so the network is never touched in tests).
 * Every method is non-throwing by contract: a hiccup yields `null` / `false`.
 */
export interface ServerSaveClient {
  /** Read the durable server save (null when absent / unreachable / unconfigured). */
  get(): Promise<{ save: Save; updatedAt: number | null } | null>;
  /** Best-effort durable write (true on a confirmed write, false otherwise). */
  put(save: Save, updatedAt: number): Promise<boolean>;
}

/** Wiring a session needs that is awkward to derive from the Save alone. */
export interface GameSessionDeps {
  /** Proxy base URL (env-configurable; localhost:8787 in dev). */
  readonly apiBaseUrl?: string;
  /** Bearer token for the proxy (user-entered; null ⇒ template floor only). */
  readonly proxyToken?: string | null;
  /** Injectable fetch (tests). Defaults to global fetch when present. */
  readonly fetchImpl?: typeof fetch;
  /** Injectable clock (tests). Defaults to Date.now. */
  readonly now?: () => number;
  /** Override the society roster size (tests use a tiny roster). */
  readonly societySize?: number;
  /** Pre-loaded dataset bundle (tests inject fixtures to stay hermetic + fast). */
  readonly datasetOverride?: DatasetBundle;
  /** Force a Voice (tests inject a stub so no network is ever touched). */
  readonly voiceOverride?: Voice;
  /**
   * Override the server-synced-save client (tests stub it). When absent, a real
   * client is derived from {@link apiBaseUrl} + {@link proxyToken} + {@link
   * fetchImpl}; if no token is configured, its calls are inert (null / false).
   */
  readonly serverSave?: ServerSaveClient;
}

/** Distill the proxy wiring (base url + token + fetch) out of the session deps. */
function serverSaveDeps(deps: GameSessionDeps) {
  return {
    ...(deps.apiBaseUrl != null ? { apiBaseUrl: deps.apiBaseUrl } : {}),
    proxyToken: deps.proxyToken ?? null,
    ...(deps.fetchImpl ? { fetchImpl: deps.fetchImpl } : {}),
  };
}

/**
 * Build the {@link ServerSaveClient} a session uses for the durable source of truth,
 * bound to `slotId` so every read/write targets this save's own slot (D16). Inert
 * (returns null / false without touching the network) when no proxy token is
 * configured — exactly the template-floor / offline shape. Never throws.
 *
 * A test-injected `deps.serverSave` stub is used verbatim (it carries its own slot
 * semantics); the real client routes through the slot-aware serverSave helpers.
 */
function makeServerSaveClient(deps: GameSessionDeps, slotId: string): ServerSaveClient {
  if (deps.serverSave) return deps.serverSave;
  const ssDeps = serverSaveDeps(deps);
  return {
    get: () => getServerSave(ssDeps, slotId),
    put: (save: Save, updatedAt: number) => putServerSave(slotId, save, updatedAt, ssDeps),
  };
}

/**
 * Load the runtime dataset (generated, else fixtures). Never throws — a fetch/parse
 * failure quietly falls back to the bundled fixtures so the town always opens.
 */
async function loadDatasetBundle(deps: GameSessionDeps): Promise<DatasetBundle> {
  if (deps.datasetOverride) return deps.datasetOverride;
  try {
    const { species, moves } = await loadDataset();
    return { dataset: null, species, moves, index: buildDataIndex(species, moves) };
  } catch {
    // loadDataset already degrades to fixtures internally; this is belt-and-braces.
    const { FIXTURE_SPECIES, FIXTURE_MOVES } = await import('@/data');
    return {
      dataset: null,
      species: FIXTURE_SPECIES,
      moves: FIXTURE_MOVES,
      index: buildDataIndex(FIXTURE_SPECIES, FIXTURE_MOVES),
    };
  }
}

/**
 * Choose a Voice: TemplateVoice always; LLMVoice (with the template floor wired
 * behind it) only when the LLM is enabled in settings AND the proxy is reachable.
 * The reachability probe is best-effort and never throws.
 */
async function chooseVoice(
  settings: Settings,
  deps: GameSessionDeps,
  worldSeed: number,
): Promise<Voice> {
  if (deps.voiceOverride) return deps.voiceOverride;

  const template = new TemplateVoice(worldSeed >>> 0);
  const token = deps.proxyToken ?? null;
  // An EMPTY base is the valid SAME-ORIGIN (prod) case: apiBaseUrl() returns ""
  // behind nginx, and LLMVoice/serverSave already treat "" as relative /api/*.
  // So only require the LLM toggle + a token here; never reject on base === "".
  const base = deps.apiBaseUrl ?? '';

  if (!settings.llmEnabled || !token) return template;

  const reachable = await probeProxy(base, deps).catch(() => false);
  if (!reachable) return template;

  return new LLMVoice({
    apiBaseUrl: base,
    token,
    ...(deps.fetchImpl ? { fetchImpl: deps.fetchImpl as never } : {}),
    fallback: template,
  });
}

/** Best-effort `GET /api/health` probe. Never throws; returns false on any failure. */
async function probeProxy(base: string, deps: GameSessionDeps): Promise<boolean> {
  const f = deps.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  if (!f) return false;
  try {
    const url = `${base.replace(/\/+$/, '')}/api/health`;
    const res = await f(url, { method: 'GET' });
    return res.ok;
  } catch {
    return false;
  }
}

export class GameSession {
  // ── live world (mutable working copy of the Save) ──────────────────────────
  private name: string;
  private readonly profileId = 'player' as const;
  /** Stable per-save identity (D16): keys this world's slot in IndexedDB + server. */
  private readonly slotId: string;
  private readonly starterSpeciesId: string;
  private playerAvatar: PlayerAvatar;
  private playerFollowerSpeciesId: string | null;
  private readonly createdAt: number;
  private readonly worldSeed: number;
  private inGameDate: string;
  private lastPlayedAt: number;
  private settings: Settings;

  private team: PokemonInstance[];
  private boxes: { id: string; name: string; contents: PokemonInstance[] }[];
  private dexSeen: Set<string>;
  private dexCaught: Set<string>;
  private ranch: Save['ranch'];
  private trainers: SimTrainer[];
  private playerGuildIds: string[];
  private lore: Save['lore'];
  /** Quest/story progress (D17). Progress only; authored defs are {@link questDefs}. */
  private quests: QuestState;
  /** Round-3 blocks (v7): held verbatim + round-tripped; arc verbs mutate them. */
  private care: CareState;
  /** Wall-clock ms of the last ambient follower-mood chat beat (throttle). */
  private lastFollowerBeatAt = 0;
  private league: LeagueState;
  private rival: RivalState | null;
  private overworldPos: OverworldPos | null;
  /** Renders quest moments into voice beats — wired lazily by Agent E (else no-op). */
  private questBeatFactory?: QuestBeatFactory;
  /**
   * Pre-rendered story exchanges (the Mewtwo thread's badge-cadence beats) waiting to
   * stream into the zone chat. Drained FIRST by {@link getChatExchange} so a freshly
   * unlocked beat lands as the next town line. Ephemeral (never persisted): the beats
   * are re-derived from badge monotonicity, so a reload never re-fires them.
   */
  private pendingStoryExchanges: ChatExchange[] = [];

  // ── wired layers ────────────────────────────────────────────────────────────
  readonly bundle: DatasetBundle;
  readonly battleDeps: BattleDeps;
  readonly voice: Voice;
  readonly templateVoice: TemplateVoice;
  readonly chatDirector: ChatDirector;
  readonly storyVoice: TemplateStoryVoice;
  private readonly nowFn: () => number;

  // ── server-synced save (durable source of truth; IndexedDB is the cache) ──────
  private readonly serverSaveClient: ServerSaveClient;
  /** Trailing-edge debounce for best-effort durable writes (collapses bursts). */
  private serverPutTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly SERVER_PUT_DEBOUNCE_MS = 1500;

  private constructor(args: {
    save: Save;
    bundle: DatasetBundle;
    voice: Voice;
    deps: GameSessionDeps;
  }) {
    const { save, bundle, voice, deps } = args;
    this.nowFn = deps.now ?? Date.now;
    this.slotId = save.profile.slotId;
    this.serverSaveClient = makeServerSaveClient(deps, this.slotId);

    this.name = save.profile.name;
    this.starterSpeciesId = save.profile.starterSpeciesId;
    this.playerAvatar = save.profile.avatar ?? { ...DEFAULT_AVATAR };
    this.playerFollowerSpeciesId =
      save.profile.followerSpeciesId ?? save.team[0]?.speciesId ?? null;
    this.createdAt = save.profile.createdAt;
    this.worldSeed = save.worldSeed;
    this.inGameDate = save.inGameDate;
    this.lastPlayedAt = save.lastPlayedAt;
    this.settings = save.settings;

    this.team = save.team.map(cloneMon);
    this.boxes = save.boxes.map((b) => ({
      id: b.id,
      name: b.name,
      contents: b.contents.map(cloneMon),
    }));
    this.dexSeen = new Set(save.dex.seen);
    this.dexCaught = new Set(save.dex.caught);
    this.ranch = save.ranch;
    this.trainers = save.society.trainers.map((t) => t); // already cloned by the tick when needed
    this.playerGuildIds = [...save.society.playerGuildIds];
    this.lore = save.lore;
    this.quests = save.quests ?? emptyQuestState();
    this.care = save.care ?? emptyCareState();
    this.league = save.league ?? emptyLeagueState();
    this.rival = save.rival ?? null;
    this.overworldPos = save.overworld ?? null;

    this.bundle = bundle;
    this.voice = voice;
    this.templateVoice = new TemplateVoice(this.worldSeed >>> 0);
    this.storyVoice = new TemplateStoryVoice(makeRng(this.worldSeed >>> 0).fork('story'));

    // The engine's data lookups — total for any species/move on the battling mons,
    // with a graceful "unknown id" guard so a stray reference never crashes a battle.
    this.battleDeps = {
      getSpecies: (id: string): SpeciesData => {
        const s = bundle.index.speciesById(id);
        if (!s) throw new Error(`GameSession.battleDeps: unknown species '${id}'`);
        return s;
      },
      getMove: (id: string): MoveData => {
        const m = bundle.index.moveById(id);
        if (!m) throw new Error(`GameSession.battleDeps: unknown move '${id}'`);
        return m;
      },
    };

    // UNLIMITED MOVESETS — load-time backfill (idempotent). The v4→v5 migration fills
    // each owned mon's full natural level-up moveset, but it can only do so when the
    // dataset was injected before the save was migrated. This re-runs the SAME pure fill
    // with the live dataset so every team + boxed mon definitely ends up with its full
    // natural moveset (and `uses` normalized) — safe to repeat (never drops/dupes).
    for (const m of this.team) fillNaturalMoveset(m, bundle.index);
    for (const box of this.boxes) for (const m of box.contents) fillNaturalMoveset(m, bundle.index);

    // The chat-director runs the LLM (or stub) primary + the template floor, all DI.
    this.chatDirector = new ChatDirector({
      llm: voice,
      template: this.templateVoice,
      now: this.nowFn,
      seed: this.worldSeed >>> 0,
    });
  }

  // ── factories ──────────────────────────────────────────────────────────────

  /**
   * Resume the saved world, or signal "no save" so the caller shows new-game.
   *
   * The **server** is the durable source of truth, IndexedDB the local cache: we
   * try {@link ServerSaveClient.get} FIRST; if the proxy holds a save we adopt it
   * and refresh the IndexedDB cache, else we fall back to the existing IndexedDB
   * load path. Either way we then diff wall-clock vs `lastPlayedAt`, replay the
   * soft-capped overnight society tick (advancing the world + collecting warm
   * welcome-back letters), bump `lastPlayedAt`, persist, and return the session.
   *
   * Never throws, and a server hiccup never loses progress: the server client is
   * non-throwing by contract, so a miss simply falls through to the local cache.
   * A corrupt or absent save (both layers) resolves to `{ session: null }`.
   */
  static async loadOrNew(deps: GameSessionDeps = {}): Promise<LoadOrNewResult> {
    // Back-compat "resume the one world" path: the server client is bound to the
    // server's DEFAULT slot (a test stub ignores the slot anyway), and the local
    // fallback reads the most-recent slot. Multi-slot pickers use loadSlot().
    const serverSaveClient = makeServerSaveClient(deps, DEFAULT_SLOT);

    // Durable source of truth first; non-throwing, so a hiccup yields null.
    const fromServer = await serverSaveClient.get().catch(() => null);
    let save = fromServer?.save ?? null;

    if (save) {
      // Refresh the local cache so an offline next-open still finds the world.
      await saveSlotToDb(save.profile.slotId, save).catch(() => {});
    } else {
      // No durable server save (absent / unreachable / unconfigured): local cache.
      save = await loadGame().catch(() => null);
    }

    if (!save) return { session: null, welcomeBack: null };

    const bundle = await loadDatasetBundle(deps);
    const voice = await chooseVoice(save.settings, deps, save.worldSeed);
    const session = new GameSession({ save, bundle, voice, deps });

    const welcomeBack = await session.replayIdle();
    await session.save();
    return { session, welcomeBack };
  }

  /**
   * Start a brand-new world: build the initial Save (newSave + a faithful starter),
   * birth the society, persist, and return the live session.
   */
  static async createNew(opts: CreateNewOptions, deps: GameSessionDeps = {}): Promise<GameSession> {
    const bundle = await loadDatasetBundle(deps);
    const now = opts.now ?? deps.now?.() ?? Date.now();
    const worldSeed = opts.worldSeed ?? Math.floor(Math.random() * 0x1_0000_0000) >>> 0;

    // D16: ALWAYS mint a NEW, unique slot — New Game never overwrites another save.
    // `slot-<createdAt>` is stable + human-legible; a random suffix guarantees two
    // New Games in the same millisecond (e.g. a fast test clock) still differ.
    const slotId = `slot-${now}-${Math.floor(Math.random() * 0x1_0000_0000).toString(36)}`;

    // Resolve the starter species (fall back to the first available if missing).
    const starterSpecies = bundle.index.speciesById(opts.starterSpeciesId) ?? bundle.species[0];
    if (!starterSpecies) {
      throw new Error('createNew: dataset has no species to build a starter from');
    }

    const starterRng = makeRng(
      (worldSeed ^ hashString(`starter|${starterSpecies.id}|${opts.trainerName}`)) >>> 0,
    );
    const starter = makeStarter({
      species: starterSpecies,
      level: opts.starterLevel ?? 5,
      playerName: opts.trainerName,
      rng: starterRng,
      getMove: (id) => bundle.index.moveById(id),
      now,
    });

    const settings: Settings = { ...DEFAULT_SETTINGS, ...opts.settings };

    const society = makeSociety(
      worldSeed,
      deps.societySize ?? DEFAULT_SOCIETY_SIZE,
      bundle.species,
    );

    // The player is a real Pokemon-game TRAINER (a Showdown sprite); the chosen starter
    // walks behind them as the follower. A legacy `gender` maps to a sensible default
    // trainer when no explicit `trainerKey` is given (male → red, female → lyra).
    const trainerKey =
      opts.trainerKey ?? (opts.gender === 'female' ? 'lyra' : DEFAULT_AVATAR.trainerKey);
    const avatar: PlayerAvatar = { trainerKey };

    const save: Save = newSave({
      name: opts.trainerName,
      slotId,
      starterSpeciesId: starterSpecies.id,
      avatar,
      followerSpeciesId: starterSpecies.id,
      worldSeed,
      now,
      team: [starter],
      settings,
    });

    // Fold in the freshly-born society. The starter is already seeded into the dex by
    // newSave (seen + caught); the human player has no Pokemon avatar to record.
    const seeded: Save = {
      ...save,
      society: { trainers: society, playerGuildIds: [] },
      dex: {
        seen: dedupe([...save.dex.seen, starterSpecies.id]),
        caught: dedupe([...save.dex.caught, starterSpecies.id]),
      },
    };

    const voice = await chooseVoice(settings, deps, worldSeed);
    const session = new GameSession({ save: seeded, bundle, voice, deps });
    await session.save();
    return session;
  }

  // ── multi-slot saves (D16: New Game never overwrites; Continue lists slots) ──

  /**
   * List every save for the Continue picker — the union of local IndexedDB slots and
   * server slots, deduped by `slotId` (a slot present in both appears once). For a
   * server-only slot we pull its full save to read the trainer/level/dex; a slot the
   * local cache already holds skips that network fetch. Each row carries enough for a
   * "(trainer) · created/last-played · level N · N caught" line. Sorted by
   * `lastPlayedAt` descending (most-recent first). Never throws — every IO boundary
   * resolves to empty/ignored on failure.
   */
  static async listSaves(deps: GameSessionDeps = {}): Promise<SaveSummary[]> {
    const ssDeps = serverSaveDeps(deps);

    // 1. Local slots (authoritative for their own metadata, no network).
    const local = await listSavesInDb().catch(() => []);
    const byId = new Map<string, Save>();
    for (const { slotId, save } of local) byId.set(slotId, save);

    // 2. Server slots. A test stub for `serverSave` has no slot-listing surface, so
    //    we list via the raw helper (inert/[] when sync is unconfigured).
    const serverSlots = deps.serverSave ? [] : await listServerSaves(ssDeps).catch(() => []);
    for (const entry of serverSlots) {
      if (byId.has(entry.slot)) continue; // local copy already has the full metadata
      const fromServer = await getServerSave(ssDeps, entry.slot).catch(() => null);
      if (fromServer?.save) byId.set(fromServer.save.profile.slotId, fromServer.save);
    }

    const summaries = [...byId.values()].map((save) => GameSession.summarize(save));
    summaries.sort((a, b) => b.lastPlayedAt - a.lastPlayedAt);
    return summaries;
  }

  /** Distill a full Save into a lightweight picker row. */
  private static summarize(save: Save): SaveSummary {
    const level = save.team.reduce((max, m) => Math.max(max, m.level), 0);
    return {
      slotId: save.profile.slotId,
      trainerName: save.profile.name,
      createdAt: save.profile.createdAt,
      lastPlayedAt: save.lastPlayedAt,
      level,
      caught: save.dex.caught.length,
    };
  }

  /**
   * Open a specific save slot (the player's Continue-picker choice). Loads it
   * server-first (the durable source of truth), falling back to the IndexedDB cache;
   * refreshes the cache from a server copy; then runs the existing soft-capped
   * idle-replay welcome-back and persists. Returns the live session, or null when the
   * slot is absent in both layers / unreadable. Never throws.
   */
  static async loadSlot(slotId: string, deps: GameSessionDeps = {}): Promise<GameSession | null> {
    const client = makeServerSaveClient(deps, slotId);

    const fromServer = await client.get().catch(() => null);
    let save = fromServer?.save ?? null;

    if (save) {
      await saveSlotToDb(save.profile.slotId, save).catch(() => {});
    } else {
      save = await loadSlotFromDb(slotId).catch(() => null);
    }
    if (!save) return null;

    const bundle = await loadDatasetBundle(deps);
    const voice = await chooseVoice(save.settings, deps, save.worldSeed);
    const session = new GameSession({ save, bundle, voice, deps });

    await session.replayIdle();
    await session.save();
    return session;
  }

  /**
   * Delete a save slot (the picker's optional delete) from BOTH the local IndexedDB
   * cache and the server. Best-effort and non-throwing: returns true when the local
   * delete completed (the world is gone from this box regardless of the server's
   * reachability). The server delete is fired too but its outcome doesn't gate the
   * result, so a delete still "works" offline.
   */
  static async deleteSlot(slotId: string, deps: GameSessionDeps = {}): Promise<boolean> {
    await deleteSlotFromDb(slotId).catch(() => {});
    if (!deps.serverSave) {
      await deleteServerSave(slotId, serverSaveDeps(deps)).catch(() => false);
    }
    return true;
  }

  // ── idle replay ──────────────────────────────────────────────────────────────

  /**
   * Replay the overnight society tick forward through the (soft-capped) elapsed
   * in-game days and compose the warm welcome-back. Advances `inGameDate` and
   * `lastPlayedAt`. A same-day return advances nothing and yields no letters.
   */
  private async replayIdle(): Promise<WelcomeBack> {
    const now = this.nowFn();
    const rawDays = wallClockDaysElapsed(this.lastPlayedAt, now);
    const days = Math.min(rawDays, SOFT_CAP_DAYS);
    const softCapped = rawDays > SOFT_CAP_DAYS;

    if (days <= 0) {
      this.lastPlayedAt = now;
      return { daysElapsed: 0, softCapped: false, letters: [] };
    }

    const toDate = addInGameDays(this.inGameDate, days);
    const tick = makeOvernightTick(this.bundle.species);
    const { trainers, beats } = tick({
      worldSeed: this.worldSeed,
      toDate,
      elapsedDays: days,
      trainers: this.trainers,
    });

    this.trainers = trainers as SimTrainer[];
    this.inGameDate = toDate;
    this.lastPlayedAt = now;
    this.nudgeRivalBadges(); // overnight: nudge the rival up to trail you (never past)

    // Follower care: ease every partner's fullness GENTLY toward the comfortable floor
    // (never below, never a stat loss) and greet the player with ONE warm follower line —
    // "missed you and dozed in the sun", never "starving" (the anti-DW1 contract).
    this.care = easeCareForAbsence(this.care, days);
    const followerWelcome = this.followerWelcomeBeat(now);
    const allBeats = followerWelcome ? [...beats, followerWelcome] : beats;

    // Phrase the warm letters through the template floor (zero tokens, instant).
    const letters = await composeWelcomeBack(
      allBeats,
      this.trainers,
      this.templateVoice,
      this.name,
    );
    return { daysElapsed: days, softCapped, letters };
  }

  /**
   * A single gentle "your partner missed you" beat for the welcome-back — warm by
   * construction (dozing in the sun, never hunger/guilt). Null when there's no follower.
   */
  private followerWelcomeBeat(now: number): Beat | null {
    const follower = this.follower;
    if (!follower) return null;
    return {
      id: `follower-welcome-${this.inGameDate}`,
      kind: 'follower-mood',
      priority: 'normal',
      subjectTrainerId: 'player',
      channel: 'zone',
      data: {
        byPlayer: true,
        warm: true,
        nickname: follower.nickname ?? follower.speciesId,
        verb: 'dozed in the warm sun and missed you',
      },
      createdAt: now,
    };
  }

  // ── persistence ────────────────────────────────────────────────────────────

  /** Reassemble the immutable Save from the live working copy. */
  toSave(): Save {
    return {
      schemaVersion: newSave().schemaVersion,
      profile: {
        id: this.profileId,
        slotId: this.slotId,
        name: this.name,
        starterSpeciesId: this.starterSpeciesId,
        avatar: this.playerAvatar,
        followerSpeciesId: this.playerFollowerSpeciesId,
        createdAt: this.createdAt,
      },
      team: this.team.map(cloneMon),
      boxes: this.boxes.map((b) => ({
        id: b.id,
        name: b.name,
        contents: b.contents.map(cloneMon),
      })),
      dex: this.dexState(),
      ranch: this.ranch,
      society: { trainers: this.trainers, playerGuildIds: [...this.playerGuildIds] },
      lore: this.lore,
      quests: this.quests,
      care: this.care,
      league: this.league,
      rival: this.rival,
      overworld: this.overworldPos,
      worldSeed: this.worldSeed,
      inGameDate: this.inGameDate,
      lastPlayedAt: this.lastPlayedAt,
      settings: this.settings,
    };
  }

  /**
   * Persist the live world. IndexedDB is written synchronously-as-today (the local
   * cache, the immediate durable-on-disk copy a tired player closing the tab relies
   * on); the server — the durable source of truth — receives a *debounced,
   * best-effort* write so rapid autosaves collapse into one network call.
   *
   * Guarded end-to-end: a storage or network hiccup never throws into a scene, and
   * a failed server write simply leaves the next save to retry. Losing one autosave
   * (cache or server) is acceptable; a crash is not.
   */
  async save(): Promise<void> {
    this.lastPlayedAt = this.nowFn();
    const snapshot = this.toSave();
    try {
      // Write to THIS world's own slot (D16) — never the legacy single key.
      await saveSlotToDb(this.slotId, snapshot);
    } catch {
      // A storage hiccup must never throw into a scene mid-session; the next save
      // (e.g. on close) will retry. Losing one autosave is acceptable; a crash isn't.
    }
    this.queueServerPut(snapshot, this.lastPlayedAt);
  }

  /**
   * Schedule a debounced, best-effort durable write to the server. Collapses a
   * burst of saves into a single network call of the latest snapshot. Fully
   * guarded — never throws, never rejects into the caller.
   */
  private queueServerPut(save: Save, updatedAt: number): void {
    if (this.serverPutTimer != null) clearTimeout(this.serverPutTimer);
    this.serverPutTimer = setTimeout(() => {
      this.serverPutTimer = null;
      void this.serverSaveClient.put(save, updatedAt).catch(() => false);
    }, GameSession.SERVER_PUT_DEBOUNCE_MS);
  }

  /**
   * Fire any pending debounced server write immediately and await it (e.g. on tab
   * close / a deliberate flush). Best-effort: resolves false on any failure, never
   * throws. A still-pending write uses the latest persisted snapshot.
   */
  async flushServerSave(): Promise<boolean> {
    if (this.serverPutTimer != null) {
      clearTimeout(this.serverPutTimer);
      this.serverPutTimer = null;
    }
    return this.serverSaveClient.put(this.toSave(), this.nowFn()).catch(() => false);
  }

  /**
   * Durably flush the live world NOW, leaving no pending timer behind — the shape a
   * tab hide/close needs (visibilitychange→hidden / beforeunload). Writes the
   * IndexedDB cache and fires the server put *immediately* (not debounced), so the
   * snapshot survives even if the page is torn down a moment later.
   *
   * Fully guarded — never throws. The IndexedDB write is awaited (the immediate
   * durable copy a tired player closing the tab relies on); the server put is fired
   * and awaited best-effort. A failure of either is swallowed: losing one autosave is
   * acceptable, a crash on close is not.
   */
  async flush(): Promise<void> {
    this.lastPlayedAt = this.nowFn();
    const snapshot = this.toSave();
    // Cancel any pending debounced write — flush() supersedes it with this snapshot.
    if (this.serverPutTimer != null) {
      clearTimeout(this.serverPutTimer);
      this.serverPutTimer = null;
    }
    try {
      await saveSlotToDb(this.slotId, snapshot);
    } catch {
      // A storage hiccup on close must never throw; the durable server copy still
      // gets its own best-effort attempt below.
    }
    await this.serverSaveClient.put(snapshot, this.lastPlayedAt).catch(() => false);
  }

  /** Serialize the current world to a backup string (export-to-file). */
  exportToString(): string {
    return exportSaveString(this.toSave());
  }

  /** Replace the live world from an imported backup string (throws on a bad file). */
  importFromString(json: string): void {
    const save = importSaveString(json);
    this.loadFromSave(save);
  }

  private loadFromSave(save: Save): void {
    this.name = save.profile.name;
    this.playerAvatar = save.profile.avatar ?? { ...DEFAULT_AVATAR };
    this.playerFollowerSpeciesId =
      save.profile.followerSpeciesId ?? save.team[0]?.speciesId ?? null;
    this.inGameDate = save.inGameDate;
    this.lastPlayedAt = save.lastPlayedAt;
    this.settings = save.settings;
    this.team = save.team.map(cloneMon);
    this.boxes = save.boxes.map((b) => ({
      id: b.id,
      name: b.name,
      contents: b.contents.map(cloneMon),
    }));
    this.dexSeen = new Set(save.dex.seen);
    this.dexCaught = new Set(save.dex.caught);
    this.ranch = save.ranch;
    this.trainers = save.society.trainers.map((t) => t);
    this.playerGuildIds = [...save.society.playerGuildIds];
    this.lore = save.lore;
    this.quests = save.quests ?? emptyQuestState();
    this.care = save.care ?? emptyCareState();
    this.league = save.league ?? emptyLeagueState();
    this.rival = save.rival ?? null;
    this.overworldPos = save.overworld ?? null;
  }

  // ── dex ────────────────────────────────────────────────────────────────────

  private dexState(): DexState {
    return { seen: [...this.dexSeen], caught: [...this.dexCaught] };
  }

  /** Record a species in the living dex. `caught` also marks it seen. */
  addToDex(speciesId: string, caught: boolean): void {
    if (!speciesId) return;
    this.dexSeen.add(speciesId);
    if (caught) this.dexCaught.add(speciesId);
  }

  hasSeen(speciesId: string): boolean {
    return this.dexSeen.has(speciesId);
  }
  hasCaught(speciesId: string): boolean {
    return this.dexCaught.has(speciesId);
  }
  get dex(): DexState {
    return this.dexState();
  }

  // ── team + boxes ───────────────────────────────────────────────────────────

  getTeam(): readonly PokemonInstance[] {
    return this.team;
  }
  getBoxes(): readonly { id: string; name: string; contents: readonly PokemonInstance[] }[] {
    return this.boxes;
  }

  /**
   * Add a mon to the active team if there's room (max 6), else deposit it into the
   * first box. Always records it in the dex as caught. Returns where it landed.
   */
  addToTeam(mon: PokemonInstance): 'team' | 'box' {
    this.addToDex(mon.speciesId, true);
    if (this.team.length < 6) {
      this.team.push(cloneMon(mon));
      return 'team';
    }
    this.depositToBox(mon);
    return 'box';
  }

  /** Deposit a mon into the first (or a named) box, creating Box 1 if absent. */
  depositToBox(mon: PokemonInstance, boxId?: string): void {
    this.addToDex(mon.speciesId, true);
    let box = boxId ? this.boxes.find((b) => b.id === boxId) : this.boxes[0];
    if (!box) {
      box = { id: 'box-1', name: 'Box 1', contents: [] };
      this.boxes.push(box);
    }
    box.contents.push(cloneMon(mon));
  }

  /**
   * Reorder the active party by moving the mon at `from` to index `to` (both clamped).
   * Pure array reshuffle of the team the player already owns — no rules, just order.
   * The caller persists.
   */
  reorderTeam(from: number, to: number): void {
    const n = this.team.length;
    if (n <= 1) return;
    const src = Math.max(0, Math.min(n - 1, from));
    const dst = Math.max(0, Math.min(n - 1, to));
    if (src === dst) return;
    const [moved] = this.team.splice(src, 1);
    this.team.splice(dst, 0, moved);
  }

  /**
   * Replace a party mon (by instance id) with an updated instance — used when a Rare
   * Candy at the Training bench evolves a mon (evolveInstance returns a NEW instance, so
   * the live team slot must be swapped). Records the new species as caught in the dex,
   * mirroring the post-battle evolution path. Returns true if it swapped. The caller persists.
   */
  replaceTeamMember(instanceId: string, evolved: PokemonInstance): boolean {
    const idx = this.team.findIndex((m) => m.id === instanceId);
    if (idx < 0) return false;
    this.team[idx] = cloneMon(evolved);
    this.addToDex(evolved.speciesId, true);
    return true;
  }

  /**
   * Move a party mon (by instance id) into the first box. Refuses to empty the party
   * (the player can never soft-lock themselves into a teamless state). Returns true if
   * it moved. The caller persists.
   */
  moveTeamToBox(instanceId: string, boxId?: string): boolean {
    if (this.team.length <= 1) return false;
    const idx = this.team.findIndex((m) => m.id === instanceId);
    if (idx < 0) return false;
    const [mon] = this.team.splice(idx, 1);
    this.depositToBox(mon, boxId);
    return true;
  }

  /**
   * Withdraw a stored mon (by instance id) into the active party if there's room
   * (max 6). Returns true if it moved. The caller persists.
   */
  moveBoxToTeam(instanceId: string): boolean {
    if (this.team.length >= 6) return false;
    for (const box of this.boxes) {
      const idx = box.contents.findIndex((m) => m.id === instanceId);
      if (idx >= 0) {
        const [mon] = box.contents.splice(idx, 1);
        this.team.push(mon);
        return true;
      }
    }
    return false;
  }

  // ── move usage (unlimited movesets) ────────────────────────────────────────

  /**
   * Tally one use of a move on an owned team mon (UNLIMITED MOVES: the battle move menu
   * sorts most-used first). Mutates the live team slot in place so the count is folded
   * into the next persist — the battle always ends with a save via {@link
   * applyBattleResult} / its persist paths, so this needs no save() of its own (and
   * avoids a write per turn). Best-effort: an unknown id or move is a quiet no-op.
   */
  recordMoveUse(instanceId: string, moveId: string): void {
    const mon = this.team.find((m) => m.id === instanceId);
    const slot = mon?.moves.find((s) => s.moveId === moveId);
    if (slot) slot.uses = (slot.uses ?? 0) + 1;
  }

  // ── post-battle ────────────────────────────────────────────────────────────

  /**
   * Fold a finished battle's outcome into the persisted world: replace team mons
   * with their post-battle versions (already leveled/evolved by core/leveling +
   * core/evolution in the battle scene), record seen species, place any caught mon,
   * and persist. The session owns persistence; the engine owns the rules.
   *
   * Never throws — unknown ids are ignored gracefully and the save is best-effort.
   */
  async applyBattleResult(outcome: BattleOutcome): Promise<void> {
    // Snapshot pre-battle levels/species so we can emit level-up + evolve quest events.
    const before = new Map(
      this.team.map((m) => [m.id, { level: m.level, speciesId: m.speciesId }]),
    );

    if (outcome.updatedTeam && outcome.updatedTeam.length > 0) {
      const byId = new Map(outcome.updatedTeam.map((m) => [m.id, m]));
      this.team = this.team.map((m) => {
        const updated = byId.get(m.id);
        return updated ? cloneMon(updated) : m;
      });
      // A mon that evolved changed its speciesId — keep the dex honest.
      for (const m of this.team) this.addToDex(m.speciesId, true);

      // Quest events: any level-up (reach-level) and any evolution (evolve).
      for (const m of outcome.updatedTeam) {
        const prev = before.get(m.id);
        if (!prev) continue;
        if (m.level > prev.level) {
          this.recordQuestEvent({ kind: 'level', speciesId: m.speciesId, level: m.level });
        }
        if (m.speciesId !== prev.speciesId) {
          this.recordQuestEvent({ kind: 'evolve', toSpeciesId: m.speciesId });
        }
      }
    }

    for (const id of outcome.seenSpeciesIds ?? []) this.addToDex(id, false);

    if (outcome.caught) {
      this.addToTeam(outcome.caught);
      const monType = this.bundle.index.speciesById(outcome.caught.speciesId)?.types[0] ?? 'normal';
      this.recordQuestEvent({ kind: 'catch', speciesId: outcome.caught.speciesId, monType });
    }

    // Battle win (catch/friendly/gym). A loss never ticks — quests never punish.
    if (outcome.battleKind && outcome.won !== false) {
      this.recordQuestEvent({
        kind: 'battle-win',
        battleKind: outcome.battleKind,
        ...(outcome.gymId ? { gymId: outcome.gymId } : {}),
      });
    }

    // Friendship from battle — finally feeding the already-shipped FRIENDSHIP_ON_* constants
    // (until Round 3, friendship was written only at creation). A win warms every team mon a
    // little; each level-up adds a bit more. Only ever UP, and a loss never subtracts — the
    // triple contract. This complements follower-care as a second friendship-raising path.
    if (outcome.battleKind && outcome.won !== false) {
      const winDelta = outcome.battleKind === 'gym' ? FRIENDSHIP_ON_GYM_WIN : FRIENDSHIP_ON_WIN;
      for (const m of this.team) m.friendship = friendshipAfter(m.friendship, winDelta);
    }
    for (const m of this.team) {
      const prev = before.get(m.id);
      if (prev && m.level > prev.level) {
        m.friendship = friendshipAfter(m.friendship, FRIENDSHIP_ON_LEVEL_UP);
      }
    }

    await this.save();
  }

  // ── quests / story (D17: auto-progress, warm turn-in, pressure-free) ─────────

  /** The authored quest definitions (read-only data; the save stores progress only). */
  get questDefs(): readonly QuestDef[] {
    return ALL_QUESTS;
  }

  /** The player's live quest PROGRESS (active + completed + earned badges). */
  get questState(): QuestState {
    return this.quests;
  }

  // ── The League: Elite Four + Champion (Round 3, elite-four.md) ───────────────
  //
  // A de-stressed gauntlet: 5 bouts back-to-back with a FULL HEAL between each, and
  // exit-anytime / resume-anytime. A win advances the bout; a loss changes nothing (you
  // resume the SAME bout, healed). Every stressor is removed — see the no-pressure audit.

  /** The League run state (boutIndex / inProgress / clears / hallOfFame). */
  get leagueState(): LeagueState {
    return this.league;
  }

  /** The member for the bout the player is currently up to (null once cleared). */
  get leagueMemberForBout(): LeagueMember | null {
    if (this.league.boutIndex >= LEAGUE_BOUT_COUNT) return null;
    return leagueMemberByOrder(this.league.boutIndex) ?? null;
  }

  /**
   * Fully restore every team mon (HP, PP, status) — the calm answer to gauntlet
   * attrition, called between League bouts (and reusable elsewhere). Persists. Never
   * throws: a heal can only ever help.
   */
  async healTeam(): Promise<void> {
    try {
      for (const mon of this.team) {
        mon.currentHp = mon.maxHp;
        mon.status = 'none';
        mon.statusTurns = 0;
        mon.moves = mon.moves.map((slot) => ({ ...slot, pp: slot.maxPp }));
      }
      await this.save();
    } catch {
      // Warm no-op — a heal must never crash a scene.
    }
  }

  /** Begin a fresh League run: underway, back at Elite 1, team fully rested. Persists. */
  async startLeagueRun(): Promise<void> {
    this.league = startLeagueRunRule(this.league);
    await this.healTeam(); // healTeam saves
  }

  /**
   * Resolve a returned bout. On a WIN the bout advances; if that was the Champion the run
   * is cleared (Hall of Fame + celebration, see recordLeagueClear) → 'cleared', otherwise
   * the team is fully rested for the next bout → 'next'. On a LOSS nothing changes and the
   * player resumes the SAME bout, healed on next entry → 'stay'. Persists. Never throws.
   */
  async advanceLeagueBout(won: boolean): Promise<'next' | 'cleared' | 'stay'> {
    try {
      if (!won) {
        // No regression, no penalty — the loss is warm words + rest. State untouched.
        return 'stay';
      }
      this.league = advanceLeagueOnWin(this.league);
      if (this.league.boutIndex >= LEAGUE_BOUT_COUNT) {
        await this.recordLeagueClear();
        return 'cleared';
      }
      await this.healTeam(); // healTeam saves
      return 'next';
    } catch {
      return 'stay';
    }
  }

  /**
   * Crown the player Champion: snapshot the team into the Hall of Fame, bump the rematch
   * counter, reset the run, drop the champion-laurel town-prop, fire a warm town-wide
   * celebration beat, and tick the (optional) `main-league` quest via a battle-win event.
   * The spec's `the-first-friend` champion codex page is NOT authored in lore/ yet — so we
   * emit only the beat + prop here (noted for the lore agent). Persists. Never throws.
   */
  async recordLeagueClear(): Promise<void> {
    try {
      const at = this.nowFn();
      const snapshot = teamSnapshot(this.team, this.inGameDate, at);
      this.league = recordLeagueClearRule(this.league, snapshot);

      // A permanent laurel over the square (a warm monument, recorded by reward).
      await this.grantReward({ kind: 'town-prop', propId: 'champion-laurel' });

      // The whole region cheers — a highlight beat into global chat.
      this.chatDirector.pushPlayerBeats('global', [championCrownedBeat(at, this.league.clears)]);

      // Tick the optional main-league quest (a no-op if it isn't authored / already done).
      this.recordQuestEvent({ kind: 'battle-win', battleKind: 'gym', gymId: 'champion' });

      await this.save();
    } catch {
      // Warmth, never load-bearing: a celebration hiccup must never break the clear.
    }
  }

  /**
   * Wire the voice layer's quest beat factory (Agent E). Until it's set, quest
   * stage-clears / completions simply don't push a chat beat — everything else works.
   */
  setQuestBeatFactory(factory: QuestBeatFactory | undefined): void {
    this.questBeatFactory = factory;
  }

  /**
   * Resolve a quest's giver to a REAL SimTrainer id (D17 convention). Side quests name
   * an `archetype:<playStyle>` giver (e.g. `archetype:breeder`); the session binds it to
   * the first `lod:'regular'` of that playStyle in the (seed-deterministic, persisted)
   * roster order — a STABLE binding for a given save — falling back to any regular. A
   * non-archetype giver (a literal trainer id, or an authored `npc-leader-*` story NPC)
   * passes through unchanged so Agent C's ✦ markers + greet flow target the right giver.
   */
  /** Where the player last stood in the walkable overworld (null = area default). */
  get overworld(): OverworldPos | null {
    return this.overworldPos;
  }

  /**
   * Persist the player's overworld position (area + x/y + facing). Called on seam
   * transitions, before battles, on movement-stop throttles, and by flush() — so
   * closing the tab mid-walk reopens exactly where you stood. Debounced via the
   * normal autosave; never throws.
   */
  saveOverworldPos(pos: OverworldPos | null): void {
    this.overworldPos = pos;
    void this.save();
  }

  questGiverId(def: QuestDef): string {
    return this.resolveGiver(def.giver);
  }

  /**
   * Resolve any trainer REF — a literal id, `archetype:<style>`, or `role:rival` — to
   * the real bound SimTrainer id (public so scenes can display real names for refs
   * carried inside objectives, e.g. the rival chain's talk-to). Literal ids pass through.
   */
  resolveGiver(giver: string): string {
    // The Warm Rival: `role:rival` binds to the pinned RivalState.trainerId, choosing +
    // persisting the rival on first resolution (rival-arc.md). Its ✦ marker + greet flow
    // then target the real regular the rival became.
    if (giver === RIVAL_ROLE) {
      return this.ensureRival()?.trainerId ?? giver;
    }
    const prefix = 'archetype:';
    if (!giver.startsWith(prefix)) return giver;
    const style = giver.slice(prefix.length);
    const regulars = this.regulars();
    const match = regulars.find((t) => t.playStyle === style);
    return (match ?? regulars[0])?.id ?? giver;
  }

  /**
   * Fold one real play event into quest progress (the single choke point, §4). Thin +
   * non-throwing: delegates the rules to the pure `advanceQuests`, stores the next
   * state, pushes a warm `quest-progress` / `quest-complete` beat for any stage-clear /
   * completion, and auto-saves when anything actually changed. Objectives tick silently
   * during normal play — the player just gets a gentle beat when a stage clears.
   */
  recordQuestEvent(event: QuestEvent): void {
    try {
      const advance = advanceQuests(
        this.quests,
        event,
        this.questDefs,
        speciesTypeLookup(this.bundle.index),
      );
      const changed = advance.next !== this.quests;
      this.quests = advance.next;

      for (const sc of advance.stageCleared) {
        this.emitQuestBeat('quest-progress', sc.questId, sc.stageIndex);
      }
      for (const questId of advance.completed) {
        this.emitQuestBeat('quest-complete', questId);
      }

      if (changed) void this.save();
    } catch {
      // Non-throwing by contract: a quest hiccup must never crash normal play.
    }
  }

  /**
   * Which not-yet-active quests are OFFERABLE right now (visibility only — never a wall).
   * Pure; grounded in the world facts only the session holds (badges, caught count,
   * friendship). Scenes read this to place the "✦" giver marker. Never throws.
   */
  offerableQuests(): readonly QuestDef[] {
    try {
      return offerableQuestsRule(this.quests, this.questDefs, this.questWorldSlice());
    } catch {
      return [];
    }
  }

  private questWorldSlice(): QuestWorldSlice {
    return buildQuestWorldSlice({
      quests: this.quests,
      caughtCount: this.dexCaught.size,
      friendshipWith: (id) => this.trainers.find((t) => t.id === id)?.friendshipWithPlayer ?? 0,
    });
  }

  /** Accept an offered quest → seed its progress at stage 0. Persists. Non-throwing. */
  acceptQuest(questId: string): boolean {
    try {
      const def = this.questDefs.find((d) => d.id === questId);
      if (!def) return false;
      const next = acceptQuestRule(this.quests, def, this.nowFn());
      if (next === this.quests) return false;
      this.quests = next;
      void this.save();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Turn in a completed quest: mark it turned-in and grant its reward (§5). The reward
   * grant persists. Returns the grant result (null when the quest id is unknown). If the
   * player never returns, nothing bad happens — the reward simply waits. Non-throwing.
   */
  async turnInQuest(questId: string, opts?: GrantRewardOpts): Promise<GrantRewardResult | null> {
    try {
      const def = this.questDefs.find((d) => d.id === questId);
      if (!def) return null;
      this.quests = turnInQuestRule(this.quests, questId);
      return await this.grantReward(def.reward, opts);
    } catch {
      return null;
    }
  }

  /**
   * Grant a quest reward DIRECTLY into the world (§5 — no money/inventory exists). Every
   * arm is additive + non-loseable: a gift-mon is built faithfully and added to team-or-
   * box + dex; a codex entry is unlocked; a badge appended (deduped); rare candies applied
   * to a chosen (or lead) mon; an evolution stone used on a first-eligible mon; a town-prop
   * / follower-cosmetic recorded via the completed quest id (scenes read it). Persists
   * after the grant. Non-throwing — a missing species / no-eligible-mon is a warm no-op.
   */
  async grantReward(reward: QuestReward, opts: GrantRewardOpts = {}): Promise<GrantRewardResult> {
    let result: GrantRewardResult = { kind: reward.kind, ok: false };
    try {
      switch (reward.kind) {
        case 'gift-mon':
          result = this.grantGiftMon(reward, opts);
          break;
        case 'codex-unlock':
          result = this.grantCodexUnlock(reward.entryId);
          break;
        case 'badge': {
          const before = this.quests.badges.length;
          result = this.grantBadge(reward.badgeId);
          await this.surfaceMewtwoProgress(before, this.quests.badges.length);
          this.nudgeRivalBadges(); // keep the rival one step behind + a happy milestone
          break;
        }
        case 'rare-candies':
          result = this.grantRareCandies(reward.count, opts);
          break;
        case 'evolution-stone':
          result = this.grantEvolutionStone(reward.itemId, opts);
          break;
        case 'town-prop':
          result = { kind: 'town-prop', propId: reward.propId, ok: true };
          break;
        case 'follower-cosmetic': {
          // Actually WEAR the cosmetic on the current follower's care record (not just
          // record it by completed-quest id), so the Care screen can show it worn.
          const follower = this.follower;
          if (follower)
            this.care = wearCosmetic(this.care, follower.id, reward.cosmeticId, this.nowFn());
          result = { kind: 'follower-cosmetic', cosmeticId: reward.cosmeticId, ok: true };
          break;
        }
      }
    } catch {
      // Warm no-op — a reward can never crash a turn-in.
    }
    await this.save();
    return result;
  }

  /** Build a gifted mon faithfully (real stats + hydrated PP + full moveset) → team/box + dex. */
  private grantGiftMon(
    reward: Extract<QuestReward, { kind: 'gift-mon' }>,
    opts: GrantRewardOpts,
  ): GrantRewardResult {
    const species = this.bundle.index.speciesById(reward.speciesId);
    if (!species) return { kind: 'gift-mon', ok: false };

    const rng = makeRng(
      (this.worldSeed ^ hashString(`gift|${reward.speciesId}|${reward.level}|${this.nowFn()}`)) >>>
        0,
    );
    const base = makeInstance(rng, species, reward.level, { id: 'player', name: this.name });

    // makeInstance mints slots with pp/maxPp = 0 (society is pure, no move data). Hydrate
    // real PP here — exactly like friendlyParty — so the gift is battle-ready.
    const gift: PokemonInstance = {
      ...base,
      nickname: reward.nickname ?? opts.nickname ?? null,
      obtainedAt: this.nowFn(),
      originalTrainer: { trainerId: 'player', trainerName: this.name, viaTrade: false },
      moves: base.moves.map((slot) => {
        const maxPp =
          slot.maxPp > 0 ? slot.maxPp : (this.bundle.index.moveById(slot.moveId)?.pp ?? 5);
        return { moveId: slot.moveId, pp: maxPp, maxPp };
      }),
    };
    fillNaturalMoveset(gift, this.bundle.index);

    const placement = this.addToTeam(gift); // records the dex catch too
    return { kind: 'gift-mon', placement, instance: gift, ok: true };
  }

  /**
   * Unlock an AUTHORED codex entry into LoreState (monotonic; deduped). Resolves over
   * the FULL codex — the ambient seeds AND the Mewtwo thread's reward-gated story pages
   * ('the-hollow-lab', 'the-reason', 'the-circuit-walked') — via {@link codexEntryById},
   * so a quest `codex-unlock` reward for a Mewtwo page grants instead of failing closed.
   * Warm no-op if the id is unknown.
   */
  private grantCodexUnlock(entryId: string): GrantRewardResult {
    const entry = codexEntryById(entryId);
    if (!entry) return { kind: 'codex-unlock', entryId, ok: false };
    if (!this.lore.entries.some((e) => e.id === entry.id)) {
      this.lore = { ...this.lore, entries: [...this.lore.entries, entry] };
    }
    return { kind: 'codex-unlock', entryId: entry.id, ok: true };
  }

  /** Append a gym badge to QuestState.badges (dedup, in earn order). */
  private grantBadge(badgeId: string): GrantRewardResult {
    if (!this.quests.badges.includes(badgeId)) {
      this.quests = { ...this.quests, badges: [...this.quests.badges, badgeId] };
    }
    return { kind: 'badge', badgeId, ok: true };
  }

  /**
   * Surface the Mewtwo Mystery's slow badge-cadence (D17; notes/story-design/main-quest.md)
   * whenever the badge count crosses one of its thresholds (1/3/5/7/8). For each newly-crossed
   * beat we (a) grant any codex page it opens, and (b) render its authored seed line through the
   * TemplateStoryVoice — which prints `data.line` verbatim, so it works with the LLM off — and
   * queue it as the next zone-chat exchange. Two thread codex pages have no beat to carry them,
   * so we grant them here by threshold: 'the-hollow-lab' at 7 badges, 'the-reason' at 8.
   *
   * Idempotent by construction: badges only ever increase and `grantBadge` dedups, so a given
   * threshold is crossed exactly once in a save's life — a reload (no badge grant) re-fires
   * nothing, and an already-held badge widens the (before, after] window to empty.
   */
  private async surfaceMewtwoProgress(before: number, after: number): Promise<void> {
    if (after <= before) return;
    try {
      const at = this.nowFn();
      const fresh = mewtwoThreadBeats(after, at).filter((b) => {
        const req = Number(b.data.requiredBadges);
        return req > before && req <= after;
      });

      // Codex pages a crossed beat carries (Beat D → 'the-hollow-lab').
      for (const beat of fresh) {
        const codexId = typeof beat.data.codexEntryId === 'string' ? beat.data.codexEntryId : '';
        if (codexId) this.grantCodexUnlock(codexId);
      }
      // Threshold-only codex pages (no beat carries them): the design docs open
      // 'the-hollow-lab' at 7 badges and 'the-reason' at 8.
      if (before < 7 && after >= 7) this.grantCodexUnlock('the-hollow-lab');
      if (before < 8 && after >= 8) this.grantCodexUnlock('the-reason');

      await this.surfaceStoryBeats(fresh);
    } catch {
      // Warmth, never load-bearing: a story hiccup must never break a badge grant.
    }
  }

  /**
   * Render lore beats through the authored {@link TemplateStoryVoice} (verbatim `data.line`)
   * and queue the resulting lines as a zone-chat exchange for {@link getChatExchange} to
   * stream. Non-throwing; an empty render simply queues nothing.
   */
  private async surfaceStoryBeats(beats: readonly LoreBeat[]): Promise<void> {
    if (beats.length === 0) return;
    // buildStoryRequest(beats, speakers, player, desiredLineCount, tokenBudget). The
    // authored TemplateStoryVoice ignores the budget and renders data.line verbatim.
    const request = buildStoryRequest(beats, [], this.playerContext([]), beats.length, 0);
    const res = await this.storyVoice.generate(request);
    if (res.lines.length > 0) {
      this.pendingStoryExchanges.push({ channel: 'zone', lines: res.lines, source: 'template' });
    }
  }

  /** Apply N Rare Candies to the chosen (or lead) mon — a juicy level burst, no inventory. */
  private grantRareCandies(count: number, opts: GrantRewardOpts): GrantRewardResult {
    const target = this.resolveTargetMon(opts.targetInstanceId);
    if (!target) return { kind: 'rare-candies', ok: false };
    const species = this.bundle.index.speciesById(target.speciesId);
    if (!species) return { kind: 'rare-candies', ok: false };
    const n = Math.max(0, Math.floor(count));
    for (let i = 0; i < n; i++) applyRareCandy(target, species); // mutates the live team slot
    this.addToDex(target.speciesId, true);
    return { kind: 'rare-candies', instance: target, ok: true };
  }

  /**
   * Use an evolution stone on the first team mon eligible under it (or the chosen one).
   * Swaps the evolved instance into the party + dex. If nothing is eligible, a warm no-op.
   */
  private grantEvolutionStone(itemId: string, opts: GrantRewardOpts): GrantRewardResult {
    const candidates = opts.targetInstanceId
      ? this.team.filter((m) => m.id === opts.targetInstanceId)
      : this.team;
    for (const mon of candidates) {
      const species = this.bundle.index.speciesById(mon.speciesId);
      if (!species) continue;
      const match = checkEvolution(mon, species, 'item', { itemId });
      if (!match) continue;
      const toSpecies = this.bundle.index.speciesById(match.toSpeciesId);
      if (!toSpecies) continue;
      const evolved = evolveInstance(mon, toSpecies);
      this.replaceTeamMember(mon.id, evolved);
      return { kind: 'evolution-stone', instance: evolved, ok: true };
    }
    return { kind: 'evolution-stone', ok: false };
  }

  /** The mon a candy/stone targets: the given instance id, else the party lead. */
  private resolveTargetMon(instanceId?: string): PokemonInstance | null {
    if (instanceId) {
      const found = this.team.find((m) => m.id === instanceId);
      if (found) return found;
    }
    return this.team[0] ?? null;
  }

  /**
   * Turn a quest stage-clear / completion into a warm chat beat and push it into the
   * live zone stream (mirrors {@link showOffInChat}'s pushPlayerBeats path). By default
   * it builds the beat straight from the chat-director's pure {@link questBeat} — carrying
   * the authored dialogue line so the TemplateVoice renders it verbatim with the LLM off —
   * so quest progress/turn-in actually reach chat. A `setQuestBeatFactory` override (tests
   * / a richer voice layer) takes precedence when present. Never load-bearing — swallowed.
   */
  private emitQuestBeat(
    kind: 'quest-progress' | 'quest-complete',
    questId: string,
    stageIndex?: number,
  ): void {
    const def = this.questDefs.find((d) => d.id === questId);
    if (!def) return;
    const giverId = this.resolveGiver(def.giver); // bind archetype givers to a real regular
    const channel: ChatChannel = 'zone';
    try {
      const factory = this.questBeatFactory;
      let beats: readonly Beat[];
      if (factory) {
        beats = factory({
          kind,
          questId,
          title: def.title,
          giver: giverId,
          channel,
          ...(stageIndex !== undefined ? { stageIndex } : {}),
        });
      } else {
        const phase: QuestPhase = kind === 'quest-complete' ? 'complete' : 'progress';
        // A whole-quest completion carries no stage index — phrase around the final stage
        // (its onComplete is the turn-in line); a stage-clear names the stage that cleared.
        const idx = stageIndex ?? Math.max(0, def.stages.length - 1);
        beats = [questBeat(def, idx, phase, giverId, this.nowFn(), channel)];
      }
      if (beats.length > 0) this.chatDirector.pushPlayerBeats(channel, beats);
    } catch {
      // A beat is warmth, never load-bearing — swallow.
    }
  }

  /**
   * Say hi to a regular in Town (Agents C/D call this from the greet menu). Emits a
   * `talk-to` quest event so "say hi to X" objectives auto-tick. Non-throwing.
   */
  greet(trainerId: string): void {
    if (!trainerId) return;
    this.recordQuestEvent({ kind: 'talk-to', trainerId });
  }

  /**
   * Set foot in a biome on an expedition (Agents C/D call this on RouteScene entry).
   * Emits a `visit-biome` quest event so "explore X" objectives auto-tick. Non-throwing.
   */
  visitBiome(biomeId: string): void {
    if (!biomeId) return;
    this.recordQuestEvent({ kind: 'visit-biome', biomeId });
  }

  /**
   * Record a completed trade with a trainer (Agents C/D call this from TradeScene).
   * Emits a `trade` quest event so trade objectives auto-tick. Non-throwing.
   */
  completeTrade(withTrainerId: string): void {
    this.recordQuestEvent({ kind: 'trade', withTrainerId });
  }

  // ── chat / story ──────────────────────────────────────────────────────────

  private playerContext(recentDeeds: readonly string[] = []): PlayerContext {
    return { playerName: this.name, recentDeeds: [...recentDeeds] };
  }

  /** A SpeakerContext for one sim trainer (what the voice layer needs to stay warm). */
  speakerContext(trainer: SimTrainer): SpeakerContext {
    return {
      trainerId: trainer.id,
      username: trainer.username,
      avatarSpeciesId: trainer.avatarSpeciesId,
      personaSummary: `${trainer.playStyle}; ${trainer.personality.traits.join(', ')}`,
      friendshipWithPlayer: trainer.friendshipWithPlayer,
      memorySnippets: trainer.memories
        .filter((m) => m.aboutId === 'player')
        .slice(-3)
        .map((m) => m.summary),
    };
  }

  /**
   * Pull the next ready chat exchange for a channel from the director buffer, or
   * null when the buffer is empty (the caller tops it up during idle via {@link
   * fillChat}). Streaming this out at a readable pace is the scene's job.
   */
  getChatExchange(channel: ChatChannel = 'zone'): ChatExchange | null {
    // Pre-rendered story beats (the Mewtwo badge cadence) jump the queue on the zone
    // channel so a freshly unlocked thread line lands as the very next town message.
    if (channel === 'zone' && this.pendingStoryExchanges.length > 0) {
      return this.pendingStoryExchanges.shift()!;
    }
    const ex = this.chatDirector.next(channel);
    if (!ex) return null;
    return { channel: ex.channel, lines: ex.lines, source: ex.source };
  }

  /**
   * Top up a channel's look-ahead buffer during idle, grounded in the present-moment
   * ambient beats at the player's current zone. Budget-aware via the director; the
   * spent budget is returned for cost transparency. Never throws.
   *
   * Returns the VoiceLines from the exchanges newly added by this fill (flattened, in
   * order) — usually ignored by idle callers (they drain the buffer via {@link
   * getChatExchange}), but used by {@link castInChat} so a caller can bubble the
   * town's fresh reaction immediately.
   */
  async fillChat(
    channel: ChatChannel,
    nodeId: string,
    targetBufferSize = 3,
    recentDeeds: readonly string[] = [],
  ): Promise<readonly VoiceLine[]> {
    const beats = liveBeats(this.trainers, nodeId);
    const roster = this.rosterForChannel(channel, nodeId).map((t) => this.speakerContext(t));

    const budget = {
      dailyTokenBudget: this.settings.dailyTokenBudget,
      tokensSpentToday: 0,
      overBudget: false,
    };

    try {
      const { added } = await this.chatDirector.fill(
        { channel, beats, targetBufferSize, budget },
        {
          rosterByChannel: { global: roster, zone: roster, guild: roster },
          player: this.playerContext(recentDeeds),
        },
      );
      return added.flatMap((ex) => ex.lines);
    } catch {
      // The director + voices are non-throwing by contract; this is belt-and-braces.
      return [];
    }
  }

  /**
   * The player speaks in chat (a typed line or a quick reply). Feeds it to the
   * chat-director as a PLAYER beat so the town responds in-character (the two-way
   * heart of D14) — not as a weak free-text deed — then tops up the buffer so the
   * reply streams out promptly. Never throws.
   */
  async sayInChat(text: string, channel: ChatChannel, nodeId: string): Promise<void> {
    const said = text.trim();
    if (said.length === 0) return;
    this.chatDirector.pushPlayerBeats(channel, [playerMessageBeat(said, Date.now(), channel)]);
    await this.fillChat(channel, nodeId, 3, [`${this.name} said "${said}" in town chat`]);
  }

  /**
   * The player shows off a team Pokémon (defaults to the follower/lead). Posts a
   * show-off beat PLUS warm two-way reaction beats from the present regulars, so the
   * NPCs admire the player's mon and ask follow-up questions about it. Pure peacocking
   * + admiration — never a comparison, never anything that makes the player feel
   * behind. Returns the species shown (or null when the team is empty). Never throws.
   */
  async showOffInChat(
    channel: ChatChannel,
    nodeId: string,
    mon: PokemonInstance | null = this.follower,
  ): Promise<string | null> {
    const star = mon ?? this.team[0] ?? null;
    if (!star) return null;
    const present = this.rosterForChannel(channel, nodeId);
    const beats: Beat[] = [
      showOffBeat(star, Date.now(), channel),
      ...playerTeamReactionBeats([star], present, nodeId),
    ];
    this.chatDirector.pushPlayerBeats(channel, beats);
    const proudName = `${this.name} proudly showed off ${star.nickname ?? star.speciesId}`;
    await this.fillChat(channel, nodeId, 3, [proudName]);
    this.recordQuestEvent({ kind: 'show-off', speciesId: star.speciesId });
    return star.speciesId;
  }

  /**
   * The player flourishes a MOVE in the tavern (mirrors {@link showOffInChat}): posts a
   * cast beat carrying the move id + type so present residents react with warm, impressed
   * admiration grounded in the SPECIFIC move/element ("Whoa, a Flamethrower indoors?!").
   * Pure spectacle + cheering — no comparison, nothing that could make the player feel
   * behind (D14). Cheap + safe to call repeatedly (the caller throttles): a missing move
   * resolves to a graceful "Normal"-typed fallback rather than erroring.
   *
   * Returns the freshly generated reaction {@link VoiceLine}s (so the caller — TownScene —
   * can bubble them immediately); an empty array when no one is present to react. The same
   * lines are also buffered through the director for the normal streaming pump. Never throws.
   */
  async castInChat(
    channel: ChatChannel,
    nodeId: string,
    move: string,
  ): Promise<readonly VoiceLine[]> {
    const md = this.bundle.index.moveById(move);
    const beat = castBeat(
      { id: move, type: md?.type ?? 'normal', ...(md?.name ? { name: md.name } : {}) },
      Date.now(),
      channel,
    );
    this.chatDirector.pushPlayerBeats(channel, [beat]);
    const label = md?.name ?? move;
    const lines = await this.fillChat(channel, nodeId, 3, [
      `${this.name} cast ${label} in the tavern`,
    ]);
    this.recordQuestEvent({ kind: 'cast-move', moveId: move, monType: md?.type ?? 'normal' });
    return lines;
  }

  /** Trainers eligible to speak on a channel (present at the node, or regulars). */
  private rosterForChannel(channel: ChatChannel, nodeId: string): readonly SimTrainer[] {
    if (channel === 'zone') {
      const here = this.trainers.filter((t) => t.homeNodeId === nodeId);
      return here.length > 0 ? here.slice(0, 12) : this.regulars().slice(0, 12);
    }
    if (channel === 'guild') {
      const mine = this.playerGuildIds;
      return this.trainers.filter((t) => t.guildId && mine.includes(t.guildId)).slice(0, 12);
    }
    return this.regulars().slice(0, 12);
  }

  /** The full-detail "regular" cast (the rotating named trainers). */
  regulars(): readonly SimTrainer[] {
    return this.trainers.filter((t) => t.lod === 'regular');
  }

  // ── the Warm Rival (rival-arc.md) — a friend always rooting for you ────────────

  /**
   * Lazily pin the rival on first need (first quest/giver resolution, gym cameo, or spar):
   * the warmest regular becomes "your first friend on the circuit", their starter mirrors
   * yours, and they start one badge behind. Idempotent — a pinned rival is returned as-is,
   * so roster growth never reshuffles them. Persists on the first pin. Returns null only
   * when there are no regulars at all (a warm no-op — no rival this session).
   */
  ensureRival(): RivalState | null {
    if (this.rival) return this.rival;
    const chosen = chooseRival(
      this.trainers,
      this.starterSpeciesId,
      this.quests.badges.length,
      this.nowFn(),
    );
    if (!chosen) return null;
    this.rival = chosen;
    void this.save();
    return this.rival;
  }

  /** The pinned rival's working state (null until first meeting). */
  get rivalState(): RivalState | null {
    return this.rival;
  }

  /** The SimTrainer the rival became (for username / avatar / portraits). */
  private rivalTrainer(): SimTrainer | undefined {
    const id = this.rival?.trainerId;
    return id ? this.trainers.find((t) => t.id === id) : undefined;
  }

  /**
   * A read-only view scenes render (name, avatar, ace species, tallies). Ensures the rival
   * is pinned first. Null when no rival could be chosen. The tallies are FLAVOR only — never
   * shown as a scoreboard (warmth audit).
   */
  get rivalView(): {
    trainerId: string;
    username: string;
    avatarSpeciesId: string;
    starterSpeciesId: string;
    aceSpeciesId: string;
    badges: number;
    sparWins: number;
    sparLosses: number;
    lastGreetedBadge: number;
  } | null {
    const rival = this.ensureRival();
    if (!rival) return null;
    const t = this.rivalTrainer();
    const party = this.rivalSpar();
    return {
      trainerId: rival.trainerId,
      username: t?.username ?? 'Your rival',
      avatarSpeciesId: t?.avatarSpeciesId ?? rival.starterSpeciesId,
      starterSpeciesId: rival.starterSpeciesId,
      aceSpeciesId: party[0]?.speciesId ?? rival.starterSpeciesId,
      badges: rival.badges,
      sparWins: rival.sparWins,
      sparLosses: rival.sparLosses,
      lastGreetedBadge: rival.lastGreetedBadge,
    };
  }

  /** A pep/congrats/offer line for the rival, deterministic per phase + tier. */
  rivalLine(phase: RivalLinePhase): string {
    const rival = this.ensureRival();
    const tier = rival?.badges ?? 0;
    const ace = this.rivalSpar()[0]?.speciesId;
    const seed =
      phase === 'spar-win' || phase === 'spar-loss'
        ? rival
          ? rival.sparWins + rival.sparLosses
          : 0
        : tier;
    return fillRivalLine(pickRivalLine(phase, seed), {
      n: this.quests.badges.length,
      rivalmon: this.speciesLabel(ace),
      pmon: this.speciesLabel(this.follower?.speciesId ?? this.team[0]?.speciesId),
    });
  }

  private speciesLabel(speciesId: string | undefined): string | undefined {
    if (!speciesId) return undefined;
    return this.bundle.index.speciesById(speciesId)?.name ?? speciesId;
  }

  /**
   * The rival's scaled spar party (a fun, close friendly match). Deterministic per (rival,
   * badge tier, player lead level) so a rematch is stable. Empty only if no rival / a thin
   * dataset lacks every species.
   */
  rivalSpar(): PokemonInstance[] {
    const rival = this.ensureRival();
    if (!rival) return [];
    return buildRivalParty({
      index: this.bundle.index,
      seed: this.worldSeed,
      rival,
      rivalTrainer: this.rivalTrainer(),
      leadLevel: sparLeadLevel(this.team),
    });
  }

  /**
   * Fold a spar result into shared history: warm tally + a memory the voice calls back to +
   * a friendship nudge (up only) + a `rival-cheer` beat. A loss is recorded exactly as
   * warmly as a win — upside only, never a penalty (rival-arc.md no-pressure audit). Persists.
   */
  recordRivalSpar(won: boolean): void {
    const rival = this.ensureRival();
    if (!rival) return;
    recordSpar(rival, this.rivalTrainer(), won, this.nowFn());
    this.rivalCheer(won ? 'spar-win' : 'spar-loss');
    // A rival spar is a friendly battle — tick any win-friendly-battles rival quest.
    if (won) this.recordQuestEvent({ kind: 'battle-win', battleKind: 'friendly' });
    void this.save();
  }

  /**
   * Meeting the rival (from the gym-anteroom cameo or a town greeting): ensures they're
   * pinned and emits a `talk-to role:rival` event so the meet/send-off rival quests tick.
   */
  meetRival(): void {
    if (!this.ensureRival()) return;
    this.recordQuestEvent({ kind: 'talk-to', trainerId: RIVAL_ROLE });
  }

  /** Has the rival already popped in at this badge tier? (Cameo fires once per tier.) */
  rivalGreetedTier(tier: number): boolean {
    const rival = this.rival;
    return !!rival && rival.lastGreetedBadge >= tier;
  }

  /** Record that the rival greeted the player at this badge tier (no repeat this tier). */
  markRivalGreeted(tier: number): void {
    const rival = this.ensureRival();
    if (!rival || rival.lastGreetedBadge >= tier) return;
    rival.lastGreetedBadge = tier;
    void this.save();
  }

  /** Push a warm `rival-cheer` beat into town chat (rooting for you, win or lose). */
  rivalCheer(_phase?: RivalLinePhase): void {
    const rival = this.rival;
    if (!rival) return;
    try {
      this.chatDirector.pushPlayerBeats('global', [
        rivalCheerBeat(rival.trainerId, 'global', this.nowFn()),
      ]);
    } catch {
      // Warmth, never load-bearing.
    }
  }

  /**
   * Keep the rival exactly one badge behind you (never ahead) and, when their count rises,
   * emit a single happy-for-both-of-you `rival-milestone` beat. Called after a badge lands
   * and on the overnight tick. Throttled to once per tier by comparing before/after.
   */
  private nudgeRivalBadges(): void {
    const rival = this.rival;
    if (!rival) return;
    const target = rivalBadgesForPlayer(this.quests.badges.length);
    if (target <= rival.badges) return; // never goes backwards, never leads
    rival.badges = target;
    try {
      this.chatDirector.pushPlayerBeats('global', [
        rivalMilestoneBeat(rival.trainerId, rival.badges, 'global', this.nowFn()),
      ]);
    } catch {
      // Warmth, never load-bearing.
    }
  }

  // ── day cadence ──────────────────────────────────────────────────────────────

  /** A deterministic RNG anchored to (worldSeed, today, route) for an expedition. */
  expeditionRng(routeId: string): Rng {
    return makeRng((this.worldSeed ^ hashString(`${this.inGameDate}|${routeId}`)) >>> 0);
  }

  /** Begin today's expedition — currently just persists the intent. */
  async setOut(): Promise<void> {
    await this.save();
  }

  // ── battle opponents (instances built faithfully; the engine owns the rules) ──

  /**
   * Build a battle-ready wild {@link PokemonInstance} from a pre-rolled encounter
   * (deterministic from the world seed + the encounter facts). Real stats via
   * core/stats, the most-recent level-up moves with real PP, full HP. Returns null
   * only if the species is missing from the dataset (the caller skips calmly).
   */
  wildInstance(enc: GeneratedEncounter): PokemonInstance | null {
    const species = this.bundle.index.speciesById(enc.speciesId);
    if (!species) return null;
    const rng = makeRng(
      (this.worldSeed ^ hashString(`wild|${this.inGameDate}|${enc.speciesId}|${enc.level}`)) >>> 0,
    );
    const ivs = {
      hp: rng.int(32),
      atk: rng.int(32),
      def: rng.int(32),
      spatk: rng.int(32),
      spdef: rng.int(32),
      spe: rng.int(32),
    };
    const evs = { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };
    const nature = rng.pick(NATURES);
    const stats = computeStats(species.baseStats, ivs, evs, enc.level, nature);
    const moves = this.movesForSpecies(species, enc.level);

    return {
      id: `wild-${enc.speciesId}-${rng.getState().toString(36)}`,
      speciesId: species.id,
      nickname: null,
      level: enc.level,
      exp: expForLevel(species.growthRate, enc.level),
      natureId: nature.id,
      ivs,
      evs,
      abilityId: species.abilities.primary,
      moves,
      currentHp: stats.hp,
      maxHp: stats.hp,
      status: 'none',
      statusTurns: 0,
      heldItemId: null,
      friendship: 70,
      shiny: enc.shiny,
      gender:
        species.genderRatio == null ? null : rng.float() < species.genderRatio ? 'female' : 'male',
      originalTrainer: { trainerId: 'wild', trainerName: 'Wild', viaTrade: false },
      obtainedAt: this.nowFn(),
    };
  }

  /**
   * A friendly sparring partner's battle party (by trainer id). The sim trainer's
   * stored team is already a list of owned instances; we hand back fresh, full-HP
   * clones so a friendly spar never disturbs the canonical society team.
   */
  friendlyParty(trainerId: string): readonly PokemonInstance[] {
    const trainer = this.trainers.find((t) => t.id === trainerId);
    if (!trainer || trainer.team.length === 0) return [];
    return trainer.team.map((m) => {
      const clone = cloneMon(m);
      clone.currentHp = clone.maxHp;
      clone.status = 'none';
      clone.statusTurns = 0;
      // Sim-trainer mons are minted in society/roster with pp/maxPp = 0 (that layer
      // is pure and has no move data). Hydrate real PP here — where the DataIndex is
      // available — so a friendly spar's opponent can actually attack. `maxPp` is
      // readonly, so rebuild any unhydrated slot rather than mutating it.
      clone.moves = clone.moves.map((slot) => {
        if (slot.maxPp > 0) return { ...slot, pp: slot.maxPp };
        const maxPp = this.bundle.index.moveById(slot.moveId)?.pp ?? 5;
        return { ...slot, pp: maxPp, maxPp };
      });
      return clone;
    });
  }

  /** The up-to-4 most-recent level-up moves a species knows by `level` (real PP). */
  private movesForSpecies(species: SpeciesData, level: number): MoveSlot[] {
    const learnable = species.learnset
      .filter((e) => e.method === 'level-up' && (e.level ?? 1) <= level)
      .sort((a, b) => (a.level ?? 1) - (b.level ?? 1))
      .slice(-4);
    const list = learnable.length > 0 ? learnable : species.learnset.slice(0, 1);
    const slots: MoveSlot[] = [];
    for (const e of list) {
      const md = this.bundle.index.moveById(e.moveId);
      const maxPp = md?.pp ?? 5;
      slots.push({ moveId: e.moveId, pp: maxPp, maxPp });
    }
    if (slots.length === 0) slots.push({ moveId: 'tackle', pp: 35, maxPp: 35 });
    return slots;
  }

  /** Advance one in-game day (the daily ritual rollover). Persists. */
  async advanceDay(): Promise<void> {
    this.inGameDate = addInGameDays(this.inGameDate, 1);
    await this.save();
  }

  // ── credits (CC-NC attribution for the spriters) ───────────────────────────

  /** Spriter credits from the dataset, for the in-game credits screen. */
  getCredits(): readonly CreditEntry[] {
    const credits = this.bundle.dataset?.spriteCredits ?? [];
    return credits.map((c) => ({
      speciesId: c.speciesId,
      contributorNames: [...c.contributorNames],
    }));
  }

  // ── accessors the scenes read ──────────────────────────────────────────────

  get trainerName(): string {
    return this.name;
  }

  /** The player's chosen Showdown trainer avatar (a `trainerKey`). */
  get avatar(): PlayerAvatar {
    return this.playerAvatar;
  }

  /** The species id of the team Pokemon following the player (null ⇒ none). */
  get followerSpeciesId(): string | null {
    return this.playerFollowerSpeciesId;
  }

  /**
   * The follower's live {@link PokemonInstance} — the team member whose species is
   * `followerSpeciesId`. Returns the first team member when the recorded follower has
   * left the party (e.g. boxed/traded) so the player is always accompanied, or null
   * only when the team is empty / no follower is set.
   */
  get follower(): PokemonInstance | null {
    if (this.team.length === 0) return null;
    if (this.playerFollowerSpeciesId == null) return null;
    return (
      this.team.find((m) => m.speciesId === this.playerFollowerSpeciesId) ?? this.team[0] ?? null
    );
  }

  /**
   * Choose which team Pokemon follows the player — by team index (a number) or by
   * species id (a string). Clamps an out-of-range index and ignores a species not on
   * the team, leaving the follower unchanged. Persists. Returns the resolved follower
   * species id (or null when the team is empty).
   */
  async setFollower(speciesIdOrTeamIndex: string | number): Promise<string | null> {
    if (this.team.length === 0) {
      this.playerFollowerSpeciesId = null;
      await this.save();
      return null;
    }
    if (typeof speciesIdOrTeamIndex === 'number') {
      const i = Math.max(0, Math.min(this.team.length - 1, Math.floor(speciesIdOrTeamIndex)));
      this.playerFollowerSpeciesId = this.team[i]!.speciesId;
    } else if (this.team.some((m) => m.speciesId === speciesIdOrTeamIndex)) {
      this.playerFollowerSpeciesId = speciesIdOrTeamIndex;
    }
    await this.save();
    return this.playerFollowerSpeciesId;
  }

  // ── follower care (Round 3; the game's first friendship-RAISING path) ────────────

  /**
   * The care record for a mon (by stable instance id), lazily created at a warm starting
   * fullness on first read and persisted. Non-throwing; the Care scene seeds its gauges from
   * this. Boxed/benched mons are welcome too — warmth is never gated to the active team.
   */
  careRecordFor(instanceId: string): { fullness: number; cosmeticId: string | null } {
    const { state, record } = ensureCareRecord(this.care, instanceId, this.nowFn());
    if (state !== this.care) {
      this.care = state;
      void this.save();
    }
    return { fullness: record.fullness, cosmeticId: record.cosmeticId };
  }

  /** The current follower's care snapshot (defaults to the resting floor / no cosmetic). */
  get followerCare(): { fullness: number; cosmeticId: string | null } {
    const mon = this.follower;
    if (!mon) return { fullness: FULLNESS_FLOOR, cosmeticId: null };
    const rec = getCareRecord(this.care, mon.id);
    return rec
      ? { fullness: rec.fullness, cosmeticId: rec.cosmeticId }
      : { fullness: FULLNESS_FLOOR, cosmeticId: null };
  }

  /**
   * Apply one warm care action to a mon (by instance id; falls back to the follower). Raises
   * the REAL {@link PokemonInstance.friendship} (the first friendship-raising path in the
   * game), eases fullness UP, stamps `lastCaredAt`, persists, and returns the deltas for the
   * Care scene to animate. Non-throwing: an unknown/absent mon yields null (a warm no-op).
   * Friendship only ever climbs and fullness never drops — pure upside, by construction.
   */
  async careAct(
    instanceId: string,
    action: CareAction,
  ): Promise<{
    emotion: Emotion;
    verb: string;
    newFriendship: number;
    newFullness: number;
  } | null> {
    const mon = this.monById(instanceId) ?? this.follower;
    if (!mon) return null;
    const now = this.nowFn();
    const { state, record } = ensureCareRecord(this.care, mon.id, now);
    const { outcome, record: nextRecord } = careActOn(record, mon.friendship, action, now);
    mon.friendship = outcome.friendship;
    this.care = upsertCareRecord(state, nextRecord);
    await this.save();
    return {
      emotion: outcome.emotion,
      verb: outcome.verb,
      newFriendship: outcome.friendship,
      newFullness: outcome.fullness,
    };
  }

  /** Find an owned mon by stable instance id across the team and every box (null if gone). */
  monById(instanceId: string): PokemonInstance | null {
    const inTeam = this.team.find((m) => m.id === instanceId);
    if (inTeam) return inTeam;
    for (const box of this.boxes) {
      const found = box.contents.find((m) => m.id === instanceId);
      if (found) return found;
    }
    return null;
  }

  /**
   * Push ONE ambient, THROTTLED `follower-mood` beat into town chat after a care act, so the
   * lively room warmly notices your well-loved partner. Warm by construction (the template
   * bank + scrubber enforce tone); best-effort and swallowed — a mood beat is never
   * load-bearing. Throttled to at most one every ~20s so repeated care never spams chat.
   */
  emitFollowerMoodBeat(channel: ChatChannel, nickname: string, emotion: Emotion): void {
    const now = this.nowFn();
    if (now - this.lastFollowerBeatAt < 20_000) return;
    this.lastFollowerBeatAt = now;
    try {
      const verb =
        emotion === 'joyous'
          ? 'is glowing with joy'
          : emotion === 'sigh'
            ? 'settled down cozily'
            : 'looks so happy';
      const beat: Beat = {
        id: `follower-mood-${now}`,
        kind: 'follower-mood',
        priority: 'ambient',
        subjectTrainerId: 'player',
        channel,
        data: { byPlayer: true, warm: true, nickname, verb },
        createdAt: now,
      };
      this.chatDirector.pushPlayerBeats(channel, [beat]);
    } catch {
      // A mood beat is pure warmth — never let it crash a care interaction.
    }
  }

  get currentDate(): string {
    return this.inGameDate;
  }
  get seed(): number {
    return this.worldSeed;
  }
  get society(): readonly SimTrainer[] {
    return this.trainers;
  }
  get currentSettings(): Settings {
    return this.settings;
  }

  /** Update settings (e.g. the Cozy↔Spicy dial, the budget dial) + persist. */
  async updateSettings(patch: Partial<Settings>): Promise<void> {
    this.settings = { ...this.settings, ...patch };
    await this.save();
  }
}

// ── helpers ────────────────────────────────────────────────────────────────────

function cloneMon(m: PokemonInstance): PokemonInstance {
  return {
    ...m,
    ivs: { ...m.ivs },
    evs: { ...m.evs },
    moves: m.moves.map((mv) => ({ ...mv })),
    originalTrainer: { ...m.originalTrainer },
  };
}

function dedupe(ids: readonly string[]): string[] {
  return [...new Set(ids.filter((id) => !!id))];
}
